import {
  Button,
  ConfirmModal,
  Flex,
  Loading,
  Table,
  type TableRef,
  Text,
  ToggleAllButton,
  useExpandedRowKeysContext,
  useTableRowSelection,
} from '@/components';
import { PublishedProjectMutableGuard } from '@/features/authentication/_components/PublishedProjectMutableGuard';
import { useProjectPermission } from '@/features/authentication/_hooks/permission';
import {
  PinpointMarker,
  PinpointMarkerForTableCell,
} from '@/features/comment/components/pinpoint';
import { pinpointTableActionColumn } from '@/features/comment/components/pinpoint/PinpointTableRowMenuCell';
import { useCommentStackSiderView } from '@/features/comment/hooks/stack-sider-view';
import { getPinpointSceneAndFieldBySheetType } from '@/features/comment/utils/pinpoint';
import { useDevToolsStorage } from '@/features/dev-tools/_hooks/local-storage';
import { useProject } from '@/features/project/_hooks/project';
import { PREVIEW_ROW_ID } from '@/features/project/_utils/constants';
import { paymentValueConverter } from '@/features/project/_utils/payment';
import type { PaymentModel } from '@/features/project/projects/_models/payment';
import type { PaymentContractModel } from '@/features/project/projects/_models/payment-contract';
import {
  useContractorPayments,
  useDeletePayment,
} from '@/features/project/projects/detail/payment/_hooks/payment';
import { useDeletePaymentContract } from '@/features/project/projects/detail/payment/_hooks/payment-contract';
import { usePaymentGroups } from '@/features/project/projects/detail/payment/_hooks/payment-group';
import { useId } from '@/hooks';
import { useLocaledAmount, useMessage } from '@/providers';
import { AmountUnitLabelModifier } from '@/utils';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';
import { usePreviewRowState } from '../../income-and-expenditure/_components/shared/PreviewRowStateProvider';
import { PaymentContractsTable } from './PaymentContractsTable';
import { PaymentFormDrawer } from './PaymentFormDrawer';

const PREVIEW_ROW: z.infer<typeof PaymentModel> = {
  id: PREVIEW_ROW_ID,
  createdAt: '',
  updatedAt: '',
  type: 'contractor',
  name: '',
  isOthers: false,
  corporateNumber: null,
  corporateAddress: null,
  corporateKind: null,
  contracts: [],
  totalContractAmount: 0,
  paymentGroupId: '',
};

export const ContractorPaymentsTable: FC = () => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();
  const { id: projectId } = useId();
  const { openMessage } = useMessage();
  const { setDrawerState } = useProjectDetailDrawerState();
  const { previewRowState, setPreviewRowState } = usePreviewRowState();
  const tableRef = useRef<TableRef>(null);
  const [paymentForDelete, setPaymentForDelete] = useState<z.infer<
    typeof PaymentModel
  > | null>(null);
  const commentStackSiderView = useCommentStackSiderView();
  const [paymentContractForDelete, setPaymentContractForDelete] =
    useState<z.infer<typeof PaymentContractModel> | null>(null);
  const paymentGroups = usePaymentGroups({ projectId });
  const payments = useContractorPayments({ projectId });
  const project = useProject(projectId);
  const { mutateAsync: deletePaymentAsync } = useDeletePayment();
  const { mutateAsync: deletePaymentContractAsync } =
    useDeletePaymentContract();
  const editPermission = useProjectPermission(true);
  const {
    expandedRowKeys,
    addExpandedRowKeys,
    removeExpandedRowKeys,
    onExpand,
  } = useExpandedRowKeysContext();
  const { selectedRowKeys, setSelectedRowKeys } = useTableRowSelection();
  const { devtoolState } = useDevToolsStorage();

  const showPreviewRow = useMemo(
    () => previewRowState?.type === 'contractor-payment',
    [previewRowState],
  );
  const tableData = showPreviewRow
    ? [...(payments.data ?? []), PREVIEW_ROW]
    : payments.data;

  useEffect(() => {
    if (showPreviewRow) {
      tableRef.current?.scrollTo({
        index: payments.data?.length,
      });
    }
  }, [showPreviewRow, payments.data]);

  if (
    payments.isPending ||
    !payments.data ||
    paymentGroups.isPending ||
    !paymentGroups.data
  ) {
    return <Loading />;
  }

  const handleClosePaymentForDeleteConfirmModal = async () => {
    setPaymentForDelete(null);
  };

  const handleClosePaymentContractForDeleteConfirmModal = async () => {
    setPaymentContractForDelete(null);
  };

  const openAllExpanded = () => {
    addExpandedRowKeys(payments.data?.map((v) => v.id) ?? []);
  };

  const closeAllExpanded = () => {
    removeExpandedRowKeys(payments.data?.map((v) => v.id) ?? []);
  };

  return (
    <Flex vertical gap="small">
      <Flex justify="space-between">
        <ToggleAllButton
          isExpandedAllWhenMounted
          title={
            <PinpointMarker
              pinpoint={getPinpointSceneAndFieldBySheetType({
                pinpointSheetType: project.data?.sheetType ?? 'RS',
                rs: {
                  field: 'rs.payment-contractor-title',
                  path: { project: projectId },
                },
                ss: {
                  field: 'ss.payment-contractor-title',
                  path: { project: projectId },
                },
              })}
            >
              <Text
                variant="h3"
                text={t('payment-contractor-list-with-unit', {
                  unit: AmountUnitLabelModifier[unit],
                })}
              />
            </PinpointMarker>
          }
          openAll={openAllExpanded}
          closeAll={closeAllExpanded}
        />
        {project.data && !project.data.isLocked && editPermission.data && (
          <PublishedProjectMutableGuard>
            <Button
              type="primary"
              onClick={() => {
                setDrawerState({
                  open: true,
                  type: 'contractor-payment',
                  isEditing: false,
                });
                setPreviewRowState({ type: 'contractor-payment' });
                setSelectedRowKeys([PREVIEW_ROW_ID]);
              }}
            >
              {t('create-contractor-payment')}
            </Button>
          </PublishedProjectMutableGuard>
        )}
      </Flex>

      <Table
        enableSticky={false}
        ref={tableRef}
        rowKey="id"
        dataSource={tableData}
        rowSelection={{
          renderCell: () => null,
          hideSelectAll: true,
          columnWidth: 0,
          selectedRowKeys,
          onChange: setSelectedRowKeys,
        }}
        columns={[
          {
            title: <Text text={t('payment-display-code')} />,
            key: 'paymentGroupId',
            dataIndex: 'paymentGroupId',
            render: (paymentGroupId, record) => {
              if (record.id === PREVIEW_ROW_ID) {
                return (
                  <Text
                    weight="regular"
                    color="secondary"
                    text={t('preview-row-description', {
                      item: t('payment-contractor'),
                    })}
                  />
                );
              }
              return paymentGroups.data?.find(
                (paymentGroup) => paymentGroup.id === paymentGroupId,
              )?.displayCode;
            },
          },
          {
            title: <Text text={t('contractor-name')} />,
            key: 'name',
            dataIndex: 'name',
            render: (value, record) => {
              if (record.id === PREVIEW_ROW_ID) {
                return null;
              }
              return (
                <PinpointMarkerForTableCell
                  filteredOrganizationFiscalYear={project.data?.fiscalYear}
                  pinpoint={
                    project.data?.sheetType === 'RS'
                      ? {
                          path: {
                            project: projectId,
                            contractorPaymentId: record.id,
                          },

                          field: 'rs.payment-contractor-row',
                        }
                      : {
                          path: {
                            project: projectId,
                            contractorPaymentId: record.id,
                          },

                          field: 'ss.payment-contractor-row',
                        }
                  }
                >
                  <Text text={value} />
                </PinpointMarkerForTableCell>
              );
            },
          },
          {
            title: <Text text={t('corporate-number')} />,
            key: 'corporateNumber',
            dataIndex: 'corporateNumber',
          },
          {
            title: (
              <Text
                text={t('contract-amount-with-unit', {
                  unit: AmountUnitLabelModifier[unit],
                })}
              />
            ),
            key: 'totalContractAmount',
            dataIndex: 'totalContractAmount',
            align: 'right',
            render: (value, record) => {
              if (record.id === PREVIEW_ROW_ID) {
                return null;
              }
              return paymentValueConverter(
                value,
                unit,
                !!devtoolState?.isDebugMode,
                record.negativeTotalContractAmountCount,
              );
            },
          },
          pinpointTableActionColumn<z.infer<typeof PaymentModel>>({
            showWithoutPinpoint: !!(
              project.data &&
              !project.data.isLocked &&
              editPermission.data
            ),
            render: (record) => ({
              pinpoint: {
                project: project.data,
                onPinpoint: () => {
                  commentStackSiderView.set({
                    payload: [
                      {
                        config: {
                          viewType: 'comment-form',
                          target: {
                            projectFiscalYear: project.data?.fiscalYear,
                          },
                          isEditing: false,
                          pinpoint:
                            project.data?.sheetType === 'RS'
                              ? {
                                  path: {
                                    project: projectId,
                                    contractorPaymentId: record.id,
                                  },

                                  field: 'rs.payment-contractor-row',
                                }
                              : {
                                  path: {
                                    project: projectId,
                                    contractorPaymentId: record.id,
                                  },

                                  field: 'ss.payment-contractor-row',
                                },
                        },
                        siderType: 'comment',
                      },
                    ],
                    target: {
                      type: 'project',
                      projectId,
                    },
                  });
                },
              },
              onEdit: () => {
                setDrawerState({
                  open: true,
                  type: 'contractor-payment',
                  isEditing: true,
                  paymentId: record.id,
                });
              },
              items: [
                {
                  key: 'delete',
                  label: t('delete'),
                  danger: true,
                  onClick: () => {
                    setPaymentForDelete(record);
                  },
                },
              ],
            }),
          }),
        ]}
        expandable={{
          expandedRowKeys,
          onExpand: (expanded, record) => {
            onExpand(expanded, record.id);
          },
          expandedRowRender: (payment) => (
            <PaymentContractsTable
              data={payment.contracts}
              paymentGroupId={payment.paymentGroupId}
              paymentId={payment.id}
              paymentType="contractor"
              onClickDelete={(value) => {
                setPaymentContractForDelete(value);
              }}
            />
          ),
          defaultExpandAllRows: true,
        }}
        pagination={false}
      />

      <PaymentFormDrawer type="contractor" />

      <ConfirmModal
        open={paymentForDelete !== null}
        title={t('delete-confirmation')}
        disclaimerText={t('delete-item-confirmation', {
          item: paymentForDelete?.name ?? '',
        })}
        onOk={async () => {
          invariant(paymentForDelete, t('unreachable-code'));

          await deletePaymentAsync({
            id: paymentForDelete.id,
            projectId,
            paymentGroupId: paymentForDelete.paymentGroupId,
          });
          await Promise.all([
            openMessage('success', t('message-delete-completed')),
            handleClosePaymentForDeleteConfirmModal(),
          ]);
        }}
        onCancel={handleClosePaymentForDeleteConfirmModal}
        skipCheck
      />

      <ConfirmModal
        open={paymentContractForDelete !== null}
        title={t('delete-confirmation')}
        disclaimerText={t('delete-item-confirmation', {
          item: paymentContractForDelete?.overview ?? '',
        })}
        onOk={async () => {
          invariant(paymentContractForDelete, t('unreachable-code'));
          const paymentGroupId = payments.data?.find(
            (payment) => payment.id === paymentContractForDelete.paymentId,
          )?.paymentGroupId;
          invariant(paymentGroupId, t('unreachable-code'));
          try {
            await deletePaymentContractAsync({
              id: paymentContractForDelete.id,
              projectId,
              paymentGroupId,
              paymentId: paymentContractForDelete.paymentId,
            });
            await Promise.all([
              openMessage('success', t('message-delete-completed')),
              handleClosePaymentContractForDeleteConfirmModal(),
            ]);
          } catch {
            await openMessage('error', t('message-delete-failed'));
          }
        }}
        onCancel={handleClosePaymentContractForDeleteConfirmModal}
        skipCheck
      />
    </Flex>
  );
};
